/**
 * The HTTP client.
 *
 * Every non-2xx response is decoded into the `ErrorResponse` the server
 * produced, so the UI reads a code and a sentence rather than parsing prose.
 * A 401 triggers exactly one refresh attempt, and concurrent 401s share it:
 * rotation is single-use, so several refreshes at once would look like a
 * replay and revoke the session. Tabs take turns under a browser lock for the
 * same reason.
 */

import type { AccessGrant, ErrorResponse } from "@prism/shared"
import { TokenStore } from "./token-store.ts"

/** A request the API refused, as something `throw` accepts. */
export class ApiError extends Error {
  readonly status: number
  readonly failure: ErrorResponse["error"]

  constructor(status: number, failure: ErrorResponse["error"]) {
    super(failure.message)
    this.name = "ApiError"
    this.status = status
    this.failure = failure
  }
}

/** A request that got no answer at all: the browser is offline, or the connection was refused or cut. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("The request got no answer.", { cause })
    this.name = "NetworkError"
  }
}

/** What a request may carry beyond its path. */
export type RequestOptions = Readonly<{
  method?: string
  /** Sent as JSON, except form data, which goes as `multipart/form-data`. */
  body?: unknown
  /** False for endpoints where a 401 is the answer, like sign-in. */
  retryOnUnauthorized?: boolean
  /** True for a read that is the same for everyone, sent without the token so a browser or a CDN may keep it. */
  isPublic?: boolean
  signal?: AbortSignal
}>

/** The one refresh in flight, shared by every 401 that arrives while it runs. */
const Refresh: { inFlight: Promise<boolean> | undefined } = { inFlight: undefined }

/** The response body of a refusal, when the server produced one. */
const isErrorResponse = (payload: unknown): payload is ErrorResponse =>
  typeof payload === "object" &&
  payload !== null &&
  "error" in payload &&
  typeof payload.error === "object" &&
  payload.error !== null &&
  "code" in payload.error &&
  typeof payload.error.code === "string" &&
  "message" in payload.error &&
  typeof payload.error.message === "string" &&
  "issues" in payload.error &&
  Array.isArray(payload.error.issues)

export const Api = {
  BASE: "/api/v1",

  /**
   * What a refusal without a readable body is reported as. A 502, 503 or 504
   * comes from the proxy in front of the API, while the API restarts or is
   * too busy to answer.
   */
  describeUnreadable: (status: number): ErrorResponse["error"] => ({
    code: "InternalError",
    message: [502, 503, 504].includes(status)
      ? "The shop is restarting or too busy to answer. Wait a few seconds and try again."
      : "The shop sent an answer this page cannot read. Reload the page and try again.",
    issues: [],
  }),

  /**
   * Whether a 401's body is a refusal that a fresh access token can cure: no
   * token, an expired or bad one, or an ended session. A wrong password or
   * code is a 401 too, and sending it again would only spend another try.
   */
  isCuredByRefresh: (payload: unknown): boolean =>
    isErrorResponse(payload) &&
    (payload.error.code === "Unauthenticated" || payload.error.code === "AccessTokenInvalid" || payload.error.code === "SessionRevoked"),

  /** The request body as fetch takes it: form data as it is, anything else as JSON, nothing as null. */
  bodyOf: (body: unknown): BodyInit | null => {
    if (body === undefined) return null
    return body instanceof FormData ? body : JSON.stringify(body)
  },

  /** Sends one request with the bearer token, if any. */
  send: async (path: string, options: RequestOptions): Promise<Response> => {
    const token = TokenStore.get()
    const headers: Record<string, string> = {}

    const isForm = options.body instanceof FormData

    // A form's content type carries its boundary, which the browser writes.
    if (options.body !== undefined && !isForm) headers["content-type"] = "application/json"
    if (token !== undefined && options.isPublic !== true) headers["authorization"] = `Bearer ${token}`

    return fetch(`${Api.BASE}${path}`, {
      method: options.method ?? "GET",
      headers,
      // The refresh and cart cookies are same-origin; Caddy puts the API behind /api.
      credentials: "same-origin",
      body: Api.bodyOf(options.body),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    }).catch((reason: unknown) => {
      // A canceled request stays what it is, so the query that canceled it can tell.
      if (reason instanceof DOMException && reason.name === "AbortError") throw reason

      throw new NetworkError(reason)
    })
  },

  /**
   * Trades the refresh cookie for a fresh access token, and resolves to
   * whether a session came back. A browser with no cookie gets a 204, which
   * means signed out. Concurrent callers share one attempt, and the tabs of
   * one browser take turns.
   */
  refresh: (): Promise<boolean> => {
    if (Refresh.inFlight !== undefined) return Refresh.inFlight

    const trade = (): Promise<Response> => fetch(`${Api.BASE}/auth/refresh`, { method: "POST", credentials: "same-origin" })

    // Under a lock every tab shares, a second tab sends the cookie the first
    // one was just given, never the one it spent, which would read as a replay.
    const traded = typeof navigator.locks === "undefined" ? trade() : navigator.locks.request("prism-refresh", trade)

    const attempt = traded
      .then(async (response) => {
        if (!response.ok) {
          await Api.discard(response)
          // Only a refused cookie ends the session; a 429, or a 502 while the
          // API restarts, leaves it and its cookie as they were.
          if (response.status === 401 || response.status === 403) {
            TokenStore.clear()
          }
          return false
        }

        if (response.status === 204) {
          TokenStore.clear()
          return false
        }

        const grant: AccessGrant = await response.json()
        TokenStore.set(grant.accessToken, grant.user)
        return true
      })
      .finally(() => {
        Refresh.inFlight = undefined
      })

    Refresh.inFlight = attempt
    return attempt
  },

  /** Sends, retries once on a 401 a refresh can cure, and throws an `ApiError` on any refusal. */
  exchange: async (path: string, options: RequestOptions): Promise<Response> => {
    let response = await Api.send(path, options)

    const isRetried =
      response.status === 401 &&
      options.retryOnUnauthorized !== false &&
      Api.isCuredByRefresh(
        await response
          .clone()
          .json()
          .catch(() => null),
      )

    if (isRetried) {
      const refreshed = await Api.refresh()
      if (refreshed) {
        await Api.discard(response)
        response = await Api.send(path, options)
      }
    }

    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null)
      throw new ApiError(response.status, isErrorResponse(payload) ? payload.error : Api.describeUnreadable(response.status))
    }

    return response
  },

  /** A request whose body is decoded as JSON. */
  request: async <A>(path: string, options: RequestOptions = {}): Promise<A> => {
    const response = await Api.exchange(path, options)
    return response.json()
  },

  /** A request whose answer carries nothing the caller reads. */
  requestEmpty: async (path: string, options: RequestOptions = {}): Promise<void> => {
    await Api.discard(await Api.exchange(path, options))
  },

  /**
   * Closes a response whose body is not read. An unread body keeps its
   * request open in the browser until it is collected.
   */
  discard: async (response: Response): Promise<void> => {
    await response.body?.cancel()
  },

  /** `path` with `query` as its query string; the bare path when `query` is empty. */
  withQuery: (path: string, query: string): string => (query.length === 0 ? path : `${path}?${query}`),

  get: <A>(path: string, signal?: AbortSignal): Promise<A> => Api.request<A>(path, signal === undefined ? {} : { signal }),

  /** A read that is the same for everyone, such as the catalog, which the browser may keep for a minute whoever is signed in. */
  getPublic: <A>(path: string, signal?: AbortSignal): Promise<A> =>
    Api.request<A>(path, signal === undefined ? { isPublic: true } : { isPublic: true, signal }),

  post: <A>(path: string, body?: unknown): Promise<A> => Api.request<A>(path, { method: "POST", body }),

  put: <A>(path: string, body?: unknown): Promise<A> => Api.request<A>(path, { method: "PUT", body }),

  delete: <A>(path: string): Promise<A> => Api.request<A>(path, { method: "DELETE" }),

  postEmpty: (path: string, body?: unknown): Promise<void> => Api.requestEmpty(path, { method: "POST", body }),

  /** For sign-in and registration, where a 401 is the answer, not a hint to retry. */
  postWithoutRetry: <A>(path: string, body?: unknown): Promise<A> =>
    Api.request<A>(path, { method: "POST", body, retryOnUnauthorized: false }),

  postEmptyWithoutRetry: (path: string, body?: unknown): Promise<void> =>
    Api.requestEmpty(path, { method: "POST", body, retryOnUnauthorized: false }),

  /** The failure inside a caught value, or `undefined` if it did not come from the API. */
  failureOf: (reason: unknown): ErrorResponse["error"] | undefined => (reason instanceof ApiError ? reason.failure : undefined),
} as const
